import { Button, Col, Row, SearchField } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCirclePlus,
  LucidePanelLeft,
  LucideSparkles,
} from 'lucide-react';

import type { ChatRecord } from '../../_graphql/queryAssistantChats';
import * as conversationState from '../../_state/chatConversation.state';
import * as chatListState from '../../_state/chatList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { ChatListItem } from './ChatListItem';

interface ChatListProps {
  onClose: () => void;
}

interface ChatListContentProps {
  chats: ChatRecord[];
  selectedChatId: string | null;
  onSelect: () => void;
  emptyMessage: string;
}

//
// ⚛️ ⚛️ ⚛️ CHAT LIST CONTENT ⚛️ ⚛️ ⚛️
//
const ChatListContent = ({
  chats,
  selectedChatId,
  onSelect,
  emptyMessage,
}: ChatListContentProps) => {
  const t = useTranslations();

  if (chats.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-white/50">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      <div className="px-4 pt-2 pb-1 text-xs font-semibold text-white/60">
        {t.sidebar.recentChats}
      </div>
      {chats.map((chat) => (
        <ChatListItem
          key={chat.id}
          chat={chat}
          isSelected={chat.id === selectedChatId}
          onSelect={onSelect}
        />
      ))}
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ CHAT LIST ⚛️ ⚛️ ⚛️
//
export const ChatList = ({ onClose }: ChatListProps) => {
  const t = useTranslations();

  // Atoms
  const { data, isLoading } = useAtomValue(
    chatListState.$chatsWithOptimisticTitles,
  );
  const selectedChatId = useAtomValue(conversationState.$selectedChatId);
  const [searchTerm, setSearchTerm] = useAtom(chatListState.$searchTerm);
  const createNewChat = useSetAtom(chatListState.$createNewChat);

  // Memoized values
  const chats = data?.chats ?? [];

  return (
    <Col className="@ChatList h-full bg-[#1e2433] text-white">
      <Row className="items-center gap-2 px-4 py-3">
        <div className="flex size-7 items-center justify-center rounded-full bg-purple shadow-[0px_4px_20px_0px_rgba(7,20,41,0.2)]">
          <LucideSparkles className="size-4 text-white" />
        </div>
        <h2 className="flex-1 text-base font-semibold">{t.sidebar.title}</h2>
        <Button
          size="icon"
          variant="ghost"
          className="rounded-md border border-white/20 text-white hover:bg-white/10"
          onPress={onClose}
          icon={<LucidePanelLeft />}
        />
      </Row>

      <Col className="gap-1.5 px-4 pb-3">
        <SearchField
          dark
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.sidebar.searchPlaceholder}
        />
        <Button
          onPress={() => {
            createNewChat();
            onClose();
          }}
          variant="solid"
          icon={<LucideCirclePlus />}
        >
          {t.sidebar.newChat}
        </Button>
      </Col>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-white/50">
            {t.sidebar.loading}
          </div>
        ) : (
          <ChatListContent
            chats={chats}
            selectedChatId={selectedChatId}
            onSelect={onClose}
            emptyMessage={t.sidebar.noChats}
          />
        )}
      </div>
    </Col>
  );
};
